import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Modal, Button } from 'react-bootstrap';
// eslint-disable-next-line import/no-duplicates
import { format, isToday, isYesterday } from 'date-fns';
// eslint-disable-next-line import/no-duplicates
import { ptBR } from 'date-fns/locale';
import {
  FiArchive,
  FiArrowLeft,
  FiCheckCircle,
  FiChevronDown,
  FiPlayCircle,
  FiRefreshCw,
  FiSend,
} from 'react-icons/fi';

import BasePage from '../../../components/BasePage';
import Message from '../../../components/Message';
import LoadingState from '../../../components/LoadingState';
import LeftSidebar from './LeftSidebar';

import api from '../../../services/api';
import { useToast } from '../../../hooks/useToast';

import {
  Card,
  ChatBody,
  ChatFooter,
  ChatHeader,
  Composer,
  ComposerHint,
  DaySeparator,
  DropdownItem,
  DropdownMenu,
  EmptyChat,
  FinishedBanner,
  HeaderActions,
  IconButton,
  Layout,
  ModalCard,
  ProtocolBlock,
  SendButton,
  StatusDropdown,
  StatusPill,
} from './styles';

interface IMessage {
  id: string;
  key_from_server: 'income' | 'outgoing';
  message_type: 'text' | 'audio' | 'document' | 'image';
  data: string;
  media_url: string;
  media_server_url: string;
  media_mime_type: string;
  media_size: number;
  media_server_size: number;
  latitude: number;
  longitude: number;
  created_at: Date;
}

interface IUser {
  id: string;
  first_name: string;
  last_name: string;
  role: string;
}

interface IComplaint {
  id: string;
  protocol: string;
  status: number;
  priority: number;
  company_id: string;
  company?: { nome_fantasia?: string };
  city?: { name?: string };
  messages: IMessage[];
  assignedUsers: IUser[];
  is_archived: boolean;
  created_at?: Date;
}

interface ILog {
  id: string;
  type: string;
  module: string;
  message: string;
  feedback: string;
  created_at: Date;
}

interface IResponse {
  complaint: IComplaint;
  logs: ILog[];
}

const STATUS_META: Record<
  number,
  { label: string; tone: 'warning' | 'info' | 'success' | 'neutral' }
> = {
  1: { label: 'Recebido', tone: 'warning' },
  2: { label: 'Em análise', tone: 'info' },
  3: { label: 'Finalizado', tone: 'success' },
};

const formatDayLabel = (date: Date): string => {
  if (isToday(date)) return 'Hoje';
  if (isYesterday(date)) return 'Ontem';
  return format(date, "EEEE, dd 'de' MMMM", { locale: ptBR });
};

const groupMessagesByDay = (messages: IMessage[]) => {
  const groups: Array<{ day: string; date: Date; items: IMessage[] }> = [];
  messages.forEach(msg => {
    const date = new Date(msg.created_at);
    const key = format(date, 'yyyy-MM-dd');
    const last = groups[groups.length - 1];
    if (last && last.day === key) {
      last.items.push(msg);
    } else {
      groups.push({ day: key, date, items: [msg] });
    }
  });
  return groups;
};

const ShowComplaint: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const { addToast } = useToast();

  const [loading, setLoading] = useState(true);
  const [complaint, setComplaint] = useState<IComplaint | undefined>();
  const [logs, setLogs] = useState<ILog[]>([]);
  const [messages, setMessages] = useState<IMessage[]>([]);
  const [currentStatus, setCurrentStatus] = useState<number>(1);

  const [composerValue, setComposerValue] = useState('');
  const [sending, setSending] = useState(false);

  const [statusMenuOpen, setStatusMenuOpen] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<number | null>(null);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [updateFeedback, setUpdateFeedback] = useState('');

  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [archiveFeedback, setArchiveFeedback] = useState('');

  const scrollRef = useRef<HTMLDivElement>(null);
  const statusMenuRef = useRef<HTMLDivElement>(null);

  const loadData = useCallback(async () => {
    const response = await api.get<IResponse>(`/complaints/${id}`);
    setComplaint(response.data.complaint);
    setMessages(response.data.complaint.messages);
    setLogs(response.data.logs || []);
    setCurrentStatus(response.data.complaint.status);
    setLoading(false);
  }, [id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!statusMenuOpen) return undefined;
    const handler = (e: MouseEvent) => {
      if (
        statusMenuRef.current &&
        !statusMenuRef.current.contains(e.target as Node)
      ) {
        setStatusMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [statusMenuOpen]);

  const sortedMessages = useMemo(() => {
    return [...messages].sort((a, b) => {
      const ta = a.created_at ? new Date(a.created_at).getTime() : 0;
      const tb = b.created_at ? new Date(b.created_at).getTime() : 0;
      return ta - tb;
    });
  }, [messages]);

  useEffect(() => {
    const node = scrollRef.current;
    if (!node) return;
    node.scrollTop = node.scrollHeight;
  }, [sortedMessages.length]);

  const groupedMessages = useMemo(
    () => groupMessagesByDay(sortedMessages),
    [sortedMessages],
  );

  const finished = currentStatus === 3;
  const archived = complaint?.is_archived;

  const handleSend = useCallback(
    async (e?: React.FormEvent) => {
      if (e) e.preventDefault();
      const trimmed = composerValue.trim();
      if (!trimmed || sending || finished) return;
      setSending(true);
      try {
        const response = await api.post<IMessage>('/messages', {
          message: trimmed,
          complaint_id: id,
        });
        setMessages(prev => [...prev, response.data]);
        setComposerValue('');
      } catch {
        addToast({
          title: 'Erro',
          body: 'Não foi possível enviar a mensagem.',
        });
      } finally {
        setSending(false);
      }
    },
    [composerValue, sending, finished, id, addToast],
  );

  const handleComposerKeyDown = (
    e: React.KeyboardEvent<HTMLTextAreaElement>,
  ) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const openStatusModal = (status: number) => {
    setPendingStatus(status);
    setShowUpdateModal(true);
    setStatusMenuOpen(false);
  };

  const handleUpdateStatus = useCallback(async () => {
    if (pendingStatus === null) return;
    try {
      await api.put(`/complaints/${id}`, {
        status: pendingStatus,
        feedback: updateFeedback,
      });
      setCurrentStatus(pendingStatus);
      setShowUpdateModal(false);
      setUpdateFeedback('');
      addToast({ title: 'Sucesso', body: 'Status atualizado.' });
      loadData();
    } catch {
      addToast({ title: 'Erro', body: 'Falha ao atualizar o status.' });
    }
  }, [pendingStatus, id, updateFeedback, addToast, loadData]);

  const handleArchive = useCallback(async () => {
    try {
      await api.put(`/complaints/${id}`, {
        is_archived: true,
        feedback: archiveFeedback,
      });
      addToast({ title: 'Sucesso', body: 'Denúncia arquivada.' });
      setShowArchiveModal(false);
      history.push('/complaints');
    } catch {
      addToast({ title: 'Erro', body: 'Falha ao arquivar a denúncia.' });
    }
  }, [id, archiveFeedback, addToast, history]);

  const handleUnarchive = useCallback(async () => {
    try {
      await api.put(`/complaints/${id}`, { is_archived: false });
      addToast({ title: 'Sucesso', body: 'Denúncia desarquivada.' });
      loadData();
    } catch {
      addToast({ title: 'Erro', body: 'Falha ao desarquivar a denúncia.' });
    }
  }, [id, addToast, loadData]);

  const statusMeta = STATUS_META[currentStatus] || STATUS_META[1];

  return (
    <BasePage title="Denúncia">
      {loading && <LoadingState />}
      {!loading && complaint && (
        <Layout>
          <Card>
            <ChatHeader>
              <IconButton
                type="button"
                onClick={() => history.push('/complaints')}
                title="Voltar"
              >
                <FiArrowLeft size={14} />
              </IconButton>

              <ProtocolBlock>
                <span className="label">Protocolo</span>
                <span className="value">{complaint.protocol}</span>
              </ProtocolBlock>

              <HeaderActions>
                <StatusDropdown ref={statusMenuRef}>
                  <StatusPill
                    type="button"
                    $tone={statusMeta.tone}
                    $clickable={!finished}
                    onClick={() => !finished && setStatusMenuOpen(prev => !prev)}
                    disabled={finished}
                  >
                    <span className="dot" />
                    {statusMeta.label}
                    {!finished && <FiChevronDown size={14} />}
                  </StatusPill>

                  {statusMenuOpen && !finished && (
                    <DropdownMenu>
                      {currentStatus === 1 && (
                        <DropdownItem
                          type="button"
                          onClick={() => openStatusModal(2)}
                        >
                          <FiPlayCircle size={14} color="#1d4ed8" />
                          Marcar como em análise
                        </DropdownItem>
                      )}
                      <DropdownItem
                        type="button"
                        onClick={() => openStatusModal(3)}
                      >
                        <FiCheckCircle size={14} color="#006633" />
                        Marcar como finalizado
                      </DropdownItem>
                    </DropdownMenu>
                  )}
                </StatusDropdown>

                {archived ? (
                  <IconButton type="button" onClick={handleUnarchive}>
                    <FiRefreshCw size={14} />
                    Desarquivar
                  </IconButton>
                ) : (
                  <IconButton
                    type="button"
                    onClick={() => setShowArchiveModal(true)}
                  >
                    <FiArchive size={14} />
                    Arquivar
                  </IconButton>
                )}
              </HeaderActions>
            </ChatHeader>

            <ChatBody ref={scrollRef}>
              {sortedMessages.length === 0 ? (
                <EmptyChat>
                  <span className="emoji">💬</span>
                  <span className="title">Nenhuma mensagem ainda</span>
                  <span>
                    Envie a primeira mensagem para iniciar o atendimento.
                  </span>
                </EmptyChat>
              ) : (
                groupedMessages.map(group => (
                  <React.Fragment key={group.day}>
                    <DaySeparator>
                      <span>{formatDayLabel(group.date)}</span>
                    </DaySeparator>
                    {group.items.map(message => (
                      <Message content={message} key={message.id} />
                    ))}
                  </React.Fragment>
                ))
              )}
            </ChatBody>

            <ChatFooter>
              {finished ? (
                <FinishedBanner>
                  <FiCheckCircle size={16} color="#006633" />
                  <span>
                    Esta denúncia foi
                    {' '}
                    <strong>finalizada</strong>
                    . Novas mensagens não podem ser enviadas.
                  </span>
                </FinishedBanner>
              ) : (
                <>
                  <Composer onSubmit={handleSend} $disabled={sending}>
                    <textarea
                      rows={1}
                      placeholder="Digite sua mensagem..."
                      value={composerValue}
                      onChange={e => setComposerValue(e.target.value)}
                      onKeyDown={handleComposerKeyDown}
                      disabled={sending}
                    />
                    <SendButton
                      type="submit"
                      disabled={!composerValue.trim() || sending}
                      aria-label="Enviar mensagem"
                    >
                      <FiSend size={16} />
                    </SendButton>
                  </Composer>
                  <ComposerHint>
                    <span>
                      Pressione
                      {' '}
                      <kbd>Enter</kbd>
                      {' '}
                      para enviar,
                      {' '}
                      <kbd>Shift</kbd>
                      {' +'}
                      <kbd>Enter</kbd>
                      {' '}
                      para quebrar linha
                    </span>
                    <span>{composerValue.length}</span>
                  </ComposerHint>
                </>
              )}
            </ChatFooter>
          </Card>

          <LeftSidebar complaint={complaint} logs={logs} />
        </Layout>
      )}

      <Modal
        show={showUpdateModal}
        onHide={() => setShowUpdateModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Atualizar status</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ModalCard>
            <p>
              Confirma a alteração do status para
              {' '}
              <strong>
                {pendingStatus !== null
                  ? STATUS_META[pendingStatus]?.label
                  : ''}
              </strong>
              ?
            </p>
            <label htmlFor="update-feedback">
              Feedback (opcional)
              <textarea
                id="update-feedback"
                placeholder="Descreva o motivo desta mudança..."
                value={updateFeedback}
                onChange={e => setUpdateFeedback(e.target.value)}
              />
            </label>
          </ModalCard>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => setShowUpdateModal(false)}
          >
            Cancelar
          </Button>
          <Button variant="success" onClick={handleUpdateStatus}>
            Atualizar
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showArchiveModal}
        onHide={() => setShowArchiveModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Arquivar denúncia</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ModalCard>
            <p>Informe o motivo do arquivamento para a TIE.</p>
            <label htmlFor="archive-feedback">
              Feedback
              <textarea
                id="archive-feedback"
                placeholder="Descreva o motivo do arquivamento..."
                value={archiveFeedback}
                onChange={e => setArchiveFeedback(e.target.value)}
              />
            </label>
          </ModalCard>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => setShowArchiveModal(false)}
          >
            Cancelar
          </Button>
          <Button variant="success" onClick={handleArchive}>
            Arquivar
          </Button>
        </Modal.Footer>
      </Modal>
    </BasePage>
  );
};

export default ShowComplaint;
